import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, Send, CheckCircle2, AlertCircle, Loader2, MapPin, Calendar, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { COMPANY_INFO, SERVICES } from '../../data/mock';

const MAPS_API_KEY = 'AIzaSyCyc0IIoR5rywlY7Ltf_9D7xnJ_6KLnIGo';

// This widget collects preferred_date/preferred_time and gates submission
// behind an inspection-fee disclaimer ("before we confirm your request") —
// it is functionally an appointment request, not a bare contact-us message,
// so it must go through the canonical booking endpoint the same as
// SchedulePage.jsx (2026-07-20). Previously posted to tech-backend's
// /api/contact, which is Lead-only (no Quotation, no calendar path) — same
// class of bug fixed on /api/schedule that day. See
// scheduler-memory/contact_form_repoint_2026_07_20.md.
const SCHEDULE_ENDPOINT = 'https://portal.aaairrigationservice.com/api/schedule-appointment';

const FRISCO_ZIPS = new Set(['75033', '75034', '75035', '75036']);
const isFrisco = (address) => {
  if (!address) return false;
  if (/\bfrisco\b/i.test(address)) return true;
  const zip = (address.match(/\b(\d{5})\b/) || [])[1];
  return zip ? FRISCO_ZIPS.has(zip) : false;
};

// Extract city/state/zip from Google Places address_components — more
// reliable than the backend's comma-split parse of the formatted address
// string, and the only way to get city/zip at all when the customer never
// selects a suggestion (see scheduler-memory/
// abbey_matatall_missing_city_zip_2026_08_03.md).
const parseAddressComponents = (components) => {
  const out = { city: '', state: '', zip: '' };
  if (!Array.isArray(components)) return out;
  for (const c of components) {
    const types = c.types || [];
    if (types.includes('locality')) out.city = c.long_name;
    else if (!out.city && types.includes('sublocality')) out.city = c.long_name;
    if (types.includes('administrative_area_level_1')) out.state = c.short_name;
    if (types.includes('postal_code')) out.zip = c.long_name;
  }
  return out;
};

export const ContactForm = ({ preselectedService = '' }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '',
    email: '',
    phone: '',
    service: preselectedService,
    address: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
    website: '', // Honeypot field
    sms_consent: false,
  });

  // True once the address was picked from a real Google Places suggestion.
  const [addressSelected, setAddressSelected] = useState(false);

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const addressRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    // Load Google Places API if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []);

  const initAutocomplete = () => {
    if (!addressRef.current || !window.google) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(addressRef.current, {
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'address_components'],
      types: ['address'],
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        const parts = parseAddressComponents(place.address_components);
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address,
          address_city: parts.city,
          address_state: parts.state,
          address_zip: parts.zip,
        }));
        setAddressSelected(true);
      }
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'address') {
      // Manual edit invalidates a previously-selected suggestion.
      setAddressSelected(false);
      setFormData(prev => ({ ...prev, address: value, address_city: '', address_state: '', address_zip: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setStatus({ loading: false, success: false, error: "Please enter both your first and last name." });
      return;
    }
    if (formData.preferred_date) {
      const dow = new Date(formData.preferred_date + 'T12:00:00').getDay();
      if (dow === 0 || dow === 6) {
        setStatus({ loading: false, success: false, error: "We don't schedule appointments on weekends. Please choose a weekday." });
        return;
      }
    }
    setShowDisclaimer(true);
  };

  const handleConfirm = async () => {
    setShowDisclaimer(false);
    setStatus({ loading: true, success: false, error: null });

    try {
      // Canonical endpoint (portal backend) — creates Lead + Quotation so the
      // request reaches Google Calendar, and sends its own confirmation +
      // office-notification emails. source: "Website" tags the internal note.
      const { message, ...rest } = formData;
      const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`;
      const payload = {
        ...rest,
        name: fullName,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        notes: message,
        source: 'Website',
      };
      const response = await fetch(SCHEDULE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        const data = await response.json();
        throw new Error(data.detail || 'Too many requests. Please try again later.');
      }

      if (!response.ok) {
        throw new Error('Failed to submit form. Please try again or call us directly.');
      }

      const data = await response.json();

      if (data.success) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ first_name: '', last_name: '', name: '', email: '', phone: '', service: '', address: '', address_city: '', address_state: '', address_zip: '', preferred_date: '', preferred_time: '', message: '', website: '', sms_consent: false });
        setAddressSelected(false);
      } else {
        throw new Error(data.message || 'Something went wrong.');
      }
    } catch (error) {
      setStatus({ loading: false, success: false, error: error.message });
    }
  };

  if (status.success) {
    return (
      <Card className="border-teal-200 bg-teal-50">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-teal-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
            <p className="text-slate-600 mb-4">We've received your request and will be in touch soon.</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 inline-block">
              <p className="text-amber-800 font-medium">Need faster response?</p>
              <a href={`tel:${COMPANY_INFO.phone}`} className="text-lg font-bold text-teal-600 hover:text-teal-700">
                Call/Text: {COMPANY_INFO.phoneFormatted}
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl text-slate-900">Request Service</CardTitle>
        <CardDescription>
          Fill out the form below and we'll get back to you within 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot field - hidden from users */}
          <div className="hidden" aria-hidden="true">
            <input type="text" name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
          </div>

          {/* Row 1: First Name + Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text" id="first_name" name="first_name" required minLength={2} maxLength={50}
                value={formData.first_name} onChange={handleChange}
                autoComplete="given-name"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="John"
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text" id="last_name" name="last_name" required minLength={2} maxLength={50}
                value={formData.last_name} onChange={handleChange}
                autoComplete="family-name"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="Doe"
              />
            </div>
          </div>

          {/* Row 2: Email (required) + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email" id="email" name="email" required
                value={formData.email} onChange={handleChange}
                autoComplete="email"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                type="tel" id="phone" name="phone" maxLength={20}
                value={formData.phone} onChange={handleChange}
                autoComplete="tel"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                placeholder="(469) 555-1234"
              />
            </div>
          </div>
          <div>
            <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-1">
              Service Needed
            </label>
            <select
              id="service" name="service"
              value={formData.service} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors bg-white"
            >
              <option value="">Select a service...</option>
              {SERVICES.map(service => (
                <option key={service.id} value={service.title}>{service.title}</option>
              ))}
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Service Address with Google Places Autocomplete */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-1">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4 text-teal-500" />
                Service Address
              </span>
            </label>
            <input
              ref={addressRef}
              type="text" id="address" name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              placeholder="Start typing your address..."
              autoComplete="off"
            />
            {formData.address && !addressSelected && (
              <p className="mt-1 text-xs text-amber-600">
                Please pick your address from the dropdown suggestions so we can confirm your city and ZIP code.
              </p>
            )}
          </div>

          {/* Preferred Date + Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cf_preferred_date" className="block text-sm font-medium text-slate-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-teal-500" />
                  Preferred Date
                </span>
              </label>
              <input
                type="date" id="cf_preferred_date" name="preferred_date"
                min={new Date().toISOString().split('T')[0]}
                value={formData.preferred_date} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="cf_preferred_time" className="block text-sm font-medium text-slate-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-4 h-4 text-teal-500" />
                  Preferred Time
                </span>
              </label>
              <select
                id="cf_preferred_time" name="preferred_time"
                value={formData.preferred_time} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors bg-white"
              >
                <option value="">Any time...</option>
                <option value="morning">Morning — 7 AM to 10 AM</option>
                <option value="midday">Midday — 10 AM to 1 PM</option>
                <option value="afternoon">Afternoon — 1 PM to 5 PM</option>
                <option value="emergency">Emergency / ASAP</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
              Message <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="message" name="message" maxLength={2000} rows={4}
              value={formData.message} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
              placeholder="Please describe your irrigation issue or service needs..."
            />
          </div>

          <p className="text-xs text-center text-slate-400 pt-2">Powered by Boatman Systems™</p>

          {/* SMS Consent */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="sms_consent"
                checked={formData.sms_consent}
                onChange={handleChange}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 flex-shrink-0"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I consent to receive appointment reminders and service updates via text message from{' '}
                <strong>AAA Irrigation Service</strong>. Msg &amp; data rates may apply. Msg frequency varies.
                Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for support at any time.{' '}
                <a href="/privacy" className="text-teal-600 hover:underline">Privacy Policy</a>
                {' '}&amp;{' '}
                <a href="/terms" className="text-teal-600 hover:underline">Terms</a>.
              </span>
            </label>
          </div>

          {status.error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{status.error}</p>
              </div>
            </div>
          )}

          <Button
            type="submit" disabled={status.loading}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 h-auto text-base shadow-lg shadow-teal-600/20 hover:shadow-teal-600/40 transition-all duration-300"
          >
            {status.loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</>
            ) : (
              <><Send className="w-5 h-5 mr-2" />Send Message</>
            )}
          </Button>

          <div className="text-center pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500 mb-2">Prefer to talk?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={`tel:${COMPANY_INFO.phone}`} className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
                <Phone className="w-4 h-4" />{COMPANY_INFO.phoneFormatted}
              </a>
              <a href={`mailto:${COMPANY_INFO.email}`} className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
                <Mail className="w-4 h-4" />Email Us
              </a>
            </div>
          </div>
        </form>
      </CardContent>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Before We Confirm Your Request</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-slate-700 space-y-3">
              <p className="font-semibold text-slate-800">
                Inspection / System Checkup — <span className="text-teal-700">{isFrisco(formData.address) ? '$125' : '$95'}</span>
              </p>
              <p className="text-amber-800 text-xs font-medium">The inspection/system checkup is a required first step before any repair work can be authorized.</p>
              <ul className="space-y-1.5 text-slate-700">
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Complete and thorough inspection of the entire system</span></li>
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Minor adjustments at no extra charge (e.g. nozzle and spray pattern adjustments)</span></li>
                <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">✓</span><span>Full repair quote provided after inspection — work only begins with your approval</span></li>
              </ul>
              <p className="text-slate-600 border-t border-amber-200 pt-2">
                You are never obligated to proceed beyond the inspection.
              </p>
              {isFrisco(formData.address) && (
                <p className="text-amber-800 font-medium">
                  Frisco, TX extended service area — fee is <strong>$125</strong>.
                </p>
              )}
              <p className="text-slate-500 text-xs">Fee is due at time of service.</p>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              By clicking <strong>I Understand — Submit Request</strong>, you acknowledge the inspection fee and agree to be contacted by AAA Irrigation Service.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisclaimer(false)}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
              >
                I Understand — Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
